// src/pages/StudioProfile.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Stack,
  Button,
  Chip,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Container,
  Divider,
  Paper,
  Avatar,
  Skeleton,
  Tooltip,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import AvailabilityList from "../components/AvailabilityList";
import ShareIcon from "@mui/icons-material/Share";
import MicExternalOnIcon from "@mui/icons-material/MicExternalOn";
import PriceChangeIcon from "@mui/icons-material/PriceChange";
import BuildIcon from "@mui/icons-material/Build";
import PlaceIcon from "@mui/icons-material/Place";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import YouTubeIcon from "@mui/icons-material/YouTube";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import EngineeringIcon from "@mui/icons-material/Engineering";
import { useNavigate, useParams } from "react-router-dom";
import { profileService, studioDirectoryService, mediaDirectoryService } from "../services/resources.service";
import { hasPermission, PERMISSIONS } from "../services/authRoles";
import { managesPage } from "../services/profilePageSetup";
import { buildRecipientInboxPath } from "../services/messaging";
import { canContactStudio, canRequestStudioSession } from "../services/proFeatures";
import DiscoveryMap from "../components/DiscoveryMap";
import FollowButton from "../components/FollowButton";
import { formatDate, getMessage, useLocale } from "../i18n/locale";
import ProfileRating from "../components/ProfileRating";
import ProFeatureControl from "../components/ProFeatureControl";
import { getPortfolioImage, PORTFOLIO_FALLBACKS } from "../services/portfolioImages";
import ProfileGallery from "../components/ProfileGallery";
import { getStudioRateDisplay } from "../services/studioRates";
import LinkedAccountBadge from "../components/LinkedAccountBadge";
import StudioExternalLinks from "../components/StudioExternalLinks";
import { BANNER_IMAGE_FALLBACK, getBannerImage, getProfileImage, imageFallbackHandler, PROFILE_IMAGE_FALLBACK } from "../services/profileImages";

async function fetchStudioById(id, service = studioDirectoryService) {
  return service.get(id);
}

async function fetchStudioReviews(id, service = studioDirectoryService) {
  return service.reviews(id);
}

async function fetchSimilarStudios(id, service = studioDirectoryService) {
  return service.similar(id);
}

// ---------------- UI helpers ----------------
function RateChip({ label, price, currency = "ZAR", locale = "en-ZA" }) {
  const amount = new Intl.NumberFormat(locale, { style: "currency", currency }).format(price);
  return (
    <Chip
      size="small"
      icon={<PriceChangeIcon />}
      label={`${label}: ${amount}`}
      variant="outlined"
    />
  );
}

function GearChip({ item }) {
  return <Chip size="small" icon={<BuildIcon />} label={item} variant="outlined" />;
}

function RoomCard({ room }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 3, height: "100%" }}>
      <CardMedia component="img" height="120" image={room.photo || BANNER_IMAGE_FALLBACK} onError={imageFallbackHandler(BANNER_IMAGE_FALLBACK)} alt={room.name} loading="lazy" />
      <CardContent>
        <Typography variant="subtitle1" fontWeight={800}>
          {room.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {room.size}
        </Typography>
      </CardContent>
    </Card>
  );
}

function PortfolioCard({ item }) {
  const isVideo = item.type === "video";
  const fallbackImage = PORTFOLIO_FALLBACKS[isVideo ? "video" : "audio"];
  return (
    <Card variant="outlined" sx={{ borderRadius: 3, height: "100%" }}>
      <CardMedia
        component="img"
        height="140"
        image={getPortfolioImage(item)}
        alt={item.title}
        loading="lazy"
        onError={(event) => {
          if (!event.currentTarget.src.endsWith(fallbackImage)) {
            event.currentTarget.src = fallbackImage;
          }
        }}
      />
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1}>
          {isVideo ? <YouTubeIcon /> : <AudiotrackIcon />}
          <Typography variant="subtitle2" fontWeight={700} noWrap>
            {item.title}
          </Typography>
        </Stack>
        <Button
          size="small"
          variant="outlined"
          sx={{ mt: 1 }}
          endIcon={<OpenInNewIcon />}
          onClick={() => window.open(item.url, "_blank", "noopener,noreferrer")}
        >
          {isVideo ? "Watch" : "Listen"}
        </Button>
      </CardContent>
    </Card>
  );
}

function SimilarProfileCard({ profile, entityType, onOpen }) {
  const { locale } = useLocale();
  const isMedia = entityType === "media";
  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <CardMedia component="img" height="110" image={profile.photo || BANNER_IMAGE_FALLBACK} onError={imageFallbackHandler(BANNER_IMAGE_FALLBACK)} alt={profile.name} loading="lazy" />
      <CardContent>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="subtitle1" fontWeight={800} noWrap>{profile.name}</Typography>
          <LinkedAccountBadge ownerUserId={profile.ownerUserId} />
        </Stack>
        <Typography variant="body2" color="text.secondary" noWrap>
          {[profile.city, getStudioRateDisplay(profile, locale)].filter(Boolean).join(" • ")}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
          {profile.services?.slice(0, 3).map((svc) => (
            <Chip key={svc} label={svc} size="small" />
          ))}
          <Chip
            size="small"
            icon={<StarRoundedIcon />}
            label={profile.rating}
            variant="outlined"
          />
        </Stack>
      </CardContent>
      <Box sx={{ px: 2, pb: 2 }}>
        <Button fullWidth variant="outlined" onClick={() => onOpen(profile.id)}>
          {getMessage(locale, isMedia ? "profileViewMedia" : "profileViewStudio")}
        </Button>
      </Box>
    </Card>
  );
}

function EngineerItem({ eng }) {
  return (
    <ListItem disableGutters sx={{ alignItems: "flex-start", py: 1 }}>
      <ListItemAvatar>
        <Avatar src={eng.avatar} alt={eng.name}>
          <EngineeringIcon />
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ flexWrap: "wrap", minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ overflowWrap: "anywhere" }}>{eng.name}</Typography>
            {eng.role && <Chip size="small" label={eng.role} sx={{ maxWidth: "100%" }} />}
            <Stack direction="row" spacing={0.5} alignItems="center">
              <StarRoundedIcon sx={{ fontSize: 16 }} />
              <Typography variant="caption">{eng.rating?.toFixed?.(1) ?? "—"}</Typography>
            </Stack>
          </Stack>
        }
        secondary={eng.links?.instagram ? "Instagram linked" : null}
      />
    </ListItem>
  );
}

// Booking dialog (MVP without date picker lib)
function BookingDialog({ open, studio, onClose, onSubmit }) {
  const [dates, setDates] = useState("");
  const [services, setServices] = useState(studio?.services?.slice(0, 2).join(", ") || "");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (open) {
      setDates("");
      setServices(studio?.services?.slice(0, 2).join(", ") || "");
      setNotes("");
    }
  }, [open, studio]);

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <DialogTitle>Request a session at “{studio?.name}”</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            label="Preferred dates"
            placeholder="e.g., 20 Oct, 23 Oct (evening)"
            value={dates}
            onChange={(e) => setDates(e.target.value)}
            fullWidth
          />
          <TextField
            label="Services"
            placeholder="Recording, Mixing"
            value={services}
            onChange={(e) => setServices(e.target.value)}
            fullWidth
          />
          <TextField
            label="Notes"
            placeholder="Band size, gear needed, references…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            minRows={4}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() =>
            onSubmit({
              proposedDates: dates,
              services: services.split(",").map((s) => s.trim()).filter(Boolean),
              message: notes,
            })
          }
        >
          Send request
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------- Page ----------------
export default function StudioProfile({ onRequireAuth, user, entityType = "studio" }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { locale } = useLocale();
  const isMedia = entityType === "media";
  const directory = isMedia ? mediaDirectoryService : studioDirectoryService;
  const basePath = isMedia ? "/media" : "/studios";
  const mayRequestSessionByRole = !isMedia && hasPermission(user, PERMISSIONS.REQUEST_STUDIO_SESSION);
  const sessionRequestLocked = mayRequestSessionByRole && !canRequestStudioSession(user);
  const sessionRequestLabel = getMessage(locale, "studioRequestSession");
  const mayContactByRole = !user || hasPermission(user, PERMISSIONS.CONTACT_STUDIO);
  const contactLocked = Boolean(user) && mayContactByRole && !canContactStudio(user);

  const [studio, setStudio] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [similar, setSimilar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOwner, setIsOwner] = useState(false);
  const [tab, setTab] = useState(0); // 0 Overview, 1 Portfolio, 2 Reviews
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "info" });
  const [bookingOpen, setBookingOpen] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const [s, r, sim] = await Promise.all([
        fetchStudioById(id, directory),
        fetchStudioReviews(id, directory),
        fetchSimilarStudios(id, directory),
      ]);
      if (!active) return;
      setStudio(s);
      setReviews(r);
      setSimilar(sim);
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [directory, id]);

  useEffect(() => {
    let active = true;
    setIsOwner(false);
    if (user?.role !== (isMedia ? "MEDIA" : "STUDIO")) return () => { active = false; };

    profileService.me()
      .then((profile) => {
        if (active) setIsOwner(managesPage(profile, isMedia ? "mediaPages" : "studioPages", id));
      })
      .catch(() => {
        if (active) setIsOwner(false);
      });

    return () => { active = false; };
  }, [id, isMedia, user?.id, user?.role]);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: studio?.name ?? getMessage(locale, isMedia ? "accountRoleMEDIA" : "accountRoleSTUDIO"), url });
      } catch {}
    } else {
      await navigator.clipboard.writeText(url);
      setSnack({ open: true, msg: "Link copied to clipboard", severity: "info" });
    }
  };

  const rateDisplay = useMemo(
    () => (studio?.rateCards?.length || studio?.rateFrom != null
      ? getStudioRateDisplay(studio, locale)
      : null),
    [studio, locale]
  );

  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: "background.default" }}>
      <AppBar position="sticky" elevation={0} color="inherit">
        <Toolbar
          sx={{
            gap: 1,
            width: "100%",
            maxWidth: 1248,
            mx: "auto",
            px: { xs: 1.5, sm: 3 },
          }}
        >
          <IconButton edge="start" aria-label="back" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ fontWeight: 900, letterSpacing: 0.2 }}>
            {isMedia ? "Media" : "Studio"}
          </Typography>
          <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
            {isOwner && (
              <Button
                variant="contained"
                size="small"
                startIcon={<EditIcon />}
                onClick={() => navigate(`${basePath}/${id}/edit`)}
              >
                {getMessage(locale, "artistProfileEdit")}
              </Button>
            )}
            <FollowButton
              subjectType={isMedia ? "media" : "studio"}
              subjectId={id}
              user={user}
              redirectTo={`${basePath}/${id}`}
              onRequireAuth={onRequireAuth}
              onFeedback={(msg, severity) => setSnack({ open: true, msg, severity })}
              followerCount={studio?.followerCount}
            />
            <Tooltip title="Share">
              <IconButton onClick={share} aria-label="share">
                <ShareIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ pt: { xs: 0, sm: 3 }, px: { xs: 0, sm: 3 } }}>
        {/* Hero */}
        <Box sx={{ position: "relative" }}>
          {loading ? (
            <Skeleton
              variant="rectangular"
              sx={{
                height: { xs: 200, sm: 280, md: 360 },
                borderRadius: { xs: 0, sm: 3 },
              }}
            />
          ) : (
            <>
              <Box
                role="img"
                aria-label={getMessage(locale, isMedia ? "profileMediaHeroAlt" : "profileStudioHeroAlt").replace("{name}", studio.name)}
                sx={{
                  height: { xs: 200, sm: 280, md: 360 },
                  borderRadius: { xs: 0, sm: 3 },
                  bgcolor: "secondary.main",
                  backgroundImage: `linear-gradient(180deg, rgba(29,33,40,.04), rgba(29,33,40,.44)), url(${getBannerImage(studio)})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
              <Avatar
                src={getProfileImage(studio)}
                imgProps={{ onError: imageFallbackHandler(PROFILE_IMAGE_FALLBACK) }}
                alt={studio.name}
                sx={{
                  width: { xs: 88, sm: 112 },
                  height: { xs: 88, sm: 112 },
                  position: "absolute",
                  left: { xs: 16, sm: 32 },
                  bottom: { xs: -44, sm: -56 },
                  border: (theme) => `4px solid ${theme.palette.background.default}`,
                  boxShadow: "0 8px 24px rgba(29,33,40,.18)",
                }}
              />
            </>
          )}
        </Box>

        <Box sx={{ pt: { xs: 6, sm: 8 }, pb: 2, px: { xs: 2, sm: 0 } }}>
        {/* Header */}
        {loading ? (
          <Stack spacing={1} sx={{ mb: 2 }}>
            <Skeleton width="40%" height={36} />
            <Skeleton width="30%" height={24} />
          </Stack>
        ) : (
          <Stack spacing={0.5} sx={{ mb: 2 }}>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Typography variant="h5" fontWeight={900}>{studio.name}</Typography>
              <LinkedAccountBadge ownerUserId={studio.ownerUserId} sx={{ fontSize: "1.35rem" }} />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              <PlaceIcon sx={{ fontSize: 16, mr: 0.5 }} />
              {studio.city} • {studio.address}
            </Typography>
            <ProfileRating rating={studio.rating} reviewCount={reviews?.length} />
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", mt: 1 }}>
              {rateDisplay && (
                <Chip size="small" icon={<PriceChangeIcon />} label={rateDisplay} />
              )}
              {studio.services.map((svc) => (
                <Chip key={svc} size="small" icon={<MicExternalOnIcon />} label={svc} />
              ))}
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
              <StudioExternalLinks studio={studio} />
              {mayRequestSessionByRole && (
                <ProFeatureControl locked={sessionRequestLocked} label={sessionRequestLabel}>
                  <span>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={sessionRequestLocked}
                      onClick={() => canRequestStudioSession(user) && setBookingOpen(true)}
                    >
                      {sessionRequestLabel}
                    </Button>
                  </span>
                </ProFeatureControl>
              )}
              {mayContactByRole && (
                <ProFeatureControl locked={contactLocked} label={getMessage(locale.locale, "studioContact")}>
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={contactLocked}
                      onClick={() => {
                        const redirectTo = buildRecipientInboxPath(isMedia ? "media" : "studio", studio.id);
                        if (!user) return onRequireAuth?.({ redirectTo, reasonKey: "loginModalMessageReason" });
                        navigate(redirectTo);
                      }}
                    >
                      {getMessage(locale.locale, "studioContact")}
                    </Button>
                  </span>
                </ProFeatureControl>
              )}
            </Stack>
          </Stack>
        )}

        {/* Tabs */}
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ px: 1, borderBottom: 1, borderColor: "divider" }}
          >
            <Tab label="Overview" />
            <Tab label={getMessage(locale.locale, "profileMedia")} />
            <Tab label="Reviews" />
          </Tabs>

          {/* Overview */}
          {tab === 0 && (
            <Box sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 8 }}>
                  {/* Rooms */}
                  <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
                    Rooms
                  </Typography>
                  {loading ? (
                    <Grid container spacing={2}>
                      {Array.from({ length: 2 }).map((_, i) => (
                        <Grid key={i} size={{ xs: 12, sm: 6 }}>
                          <Skeleton variant="rounded" height={180} sx={{ borderRadius: 3 }} />
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Grid container spacing={2}>
                      {(studio.rooms || []).map((room, i) => (
                        <Grid key={i} size={{ xs: 12, sm: 6 }}>
                          <RoomCard room={room} />
                        </Grid>
                      ))}
                    </Grid>
                  )}

                  {/* Map */}
                  <Paper variant="outlined" sx={{ borderRadius: 3, p: 2, mt: 2 }}>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
                      Location
                    </Typography>
                    {loading ? (
                      <Skeleton variant="rounded" height={220} sx={{ borderRadius: 2 }} />
                    ) : (
                      <DiscoveryMap
                        items={[studio]}
                        getCoordinates={(item) => item.geo}
                        getLabel={(item) => item.name}
                        labelMessageKey={isMedia ? "mediaMapLabel" : "studioMapLabel"}
                        height={260}
                      />
                    )}
                  </Paper>

                  {/* Policies */}
                  <Paper variant="outlined" sx={{ borderRadius: 3, p: 2, mt: 2 }}>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
                      Policies
                    </Typography>
                    {loading ? (
                      <>
                        <Skeleton height={18} />
                        <Skeleton height={18} width="85%" />
                      </>
                    ) : (
                      <Typography variant="body1">{studio.policies}</Typography>
                    )}
                  </Paper>
                </Grid>

                {/* Sidebar */}
                <Grid size={{ xs: 12, md: 4 }}>
                  {/* Rates */}
                  <Paper variant="outlined" sx={{ borderRadius: 3, p: 2 }}>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
                      Rates
                    </Typography>
                    {loading ? (
                      <Stack spacing={1}>
                        <Skeleton height={20} width="70%" />
                        <Skeleton height={20} width="50%" />
                      </Stack>
                    ) : (
                      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                        {(studio.rateCards || []).map((r, idx) => (
                          <RateChip key={idx} label={r.label} price={r.price} currency={studio.currency} locale={locale.locale} />
                        ))}
                      </Stack>
                    )}
                  </Paper>

                  {/* Gear */}
                  <Paper variant="outlined" sx={{ borderRadius: 3, p: 2, mt: 2 }}>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
                      Gear highlights
                    </Typography>
                    {loading ? (
                      <Skeleton variant="rounded" height={90} sx={{ borderRadius: 2 }} />
                    ) : (
                      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                        {(studio.gear || []).slice(0, 12).map((g, i) => (
                          <GearChip key={i} item={g} />
                        ))}
                      </Stack>
                    )}
                  </Paper>

                  {/* Engineers */}
                  <Paper variant="outlined" sx={{ borderRadius: 3, p: 2, mt: 2 }}>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
                      {getMessage(locale.locale, "createPageEngineers")}
                    </Typography>
                    {loading ? (
                      <Stack spacing={1}>
                        <Skeleton height={56} />
                        <Skeleton height={56} />
                      </Stack>
                    ) : (
                      <List dense disablePadding>
                        {(studio.engineers || []).map((e) => (
                          <EngineerItem key={e.id} eng={e} />
                        ))}
                      </List>
                    )}
                  </Paper>

                  {/* Availability */}
                  <Paper variant="outlined" sx={{ borderRadius: 3, p: 2, mt: 2 }}>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
                      Availability
                    </Typography>
                    {loading ? (
                      <Skeleton variant="rounded" height={90} sx={{ borderRadius: 2 }} />
                    ) : (
                      <AvailabilityList availability={studio.availability} locale={locale} timeZone={studio.timeZone} />
                    )}
                    {mayRequestSessionByRole && (
                      <Box sx={{ mt: 1.5 }}>
                        <ProFeatureControl locked={sessionRequestLocked} label={sessionRequestLabel}>
                          <span>
                            <Button
                              fullWidth
                              variant="contained"
                              disabled={sessionRequestLocked}
                              onClick={() => canRequestStudioSession(user) && setBookingOpen(true)}
                            >
                              {sessionRequestLabel}
                            </Button>
                          </span>
                        </ProFeatureControl>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Portfolio */}
          {tab === 1 && (
            <Box sx={{ p: 2 }}>
              {loading ? (
                <Grid container spacing={2}>
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
                      <Skeleton variant="rounded" height={200} sx={{ borderRadius: 3 }} />
                    </Grid>
                  ))}
                </Grid>
              ) : studio.photos?.length || studio.portfolio?.length ? (
                <Stack spacing={3}>
                  <ProfileGallery photos={studio.photos} />
                  {studio.portfolio?.length ? (
                    <Grid container spacing={2}>
                      {studio.portfolio.map((item, i) => (
                        <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
                          <PortfolioCard item={item} />
                        </Grid>
                      ))}
                    </Grid>
                  ) : null}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {getMessage(locale.locale, "profileNoMedia")}
                </Typography>
              )}
            </Box>
          )}

          {/* Reviews */}
          {tab === 2 && (
            <Box sx={{ p: 2 }}>
              {loading ? (
                <Stack spacing={1}>
                  <Skeleton height={72} />
                  <Skeleton height={72} />
                </Stack>
              ) : reviews?.length ? (
                <List>
                  {reviews.map((r) => (
                    <React.Fragment key={r.id}>
                      <ListItem alignItems="flex-start">
                        <ListItemAvatar>
                          <Avatar>
                            <StarRoundedIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                              <Typography variant="subtitle2">{r.reviewer.name}</Typography>
                              <Chip size="small" label={`Prof ${r.ratings.professionalism}★`} />
                              <Chip size="small" label={`Punct ${r.ratings.punctuality}★`} />
                              <Chip size="small" label={`Sound ${r.ratings.sound}★`} />
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                {formatDate(r.createdAt, { locale })}
                              </Typography>
                            </Stack>
                          }
                          secondary={r.text}
                        />
                      </ListItem>
                      <Divider component="li" />
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No reviews yet.
                </Typography>
              )}
            </Box>
          )}
        </Paper>

        {/* Similar profiles */}
        <Paper variant="outlined" sx={{ borderRadius: 3, p: 2, mt: 2 }}>
          <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
            {getMessage(locale.locale, isMedia ? "profileSimilarMedia" : "profileSimilarStudios")}
          </Typography>
          {loading ? (
            <Grid container spacing={2}>
              {Array.from({ length: 2 }).map((_, i) => (
                <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Skeleton variant="rounded" height={140} sx={{ borderRadius: 3 }} />
                </Grid>
              ))}
            </Grid>
          ) : similar?.length ? (
            <Grid container spacing={2}>
              {similar.map((s) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={s.id}>
                  <SimilarProfileCard profile={s} entityType={entityType} onOpen={(sid) => navigate(`${basePath}/${sid}`)} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography variant="body2" color="text.secondary">
              None found.
            </Typography>
          )}
        </Paper>

        {/* Booking dialog */}
        <BookingDialog
          open={bookingOpen && canRequestStudioSession(user)}
          studio={studio}
          onClose={() => setBookingOpen(false)}
          onSubmit={(payload) => {
            if (!canRequestStudioSession(user)) return;
            setBookingOpen(false);
            // In real app: POST /sessions { studioId, artistId, ...payload }
            setSnack({ open: true, msg: "Session request sent!", severity: "success" });
          }}
        />

        {/* Snackbar */}
        <Snackbar
          open={snack.open}
          autoHideDuration={2200}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity={snack.severity} sx={{ width: "100%" }}>
            {snack.msg}
          </Alert>
        </Snackbar>

        <Box sx={{ height: 16 }} />
        </Box>
      </Container>
    </Box>
  );
}
